// showActivityOption returns JSX, and babel.config.js uses the classic
// preset-react runtime, so React must be in scope here.
import React from 'react';
import axios from 'axios';
import { presaleGroupLocked } from '../utils/presaleWindows';

import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';
import { pricePointsError, quantityPricePoint, soldCountThreshold } from '../utils/ticketPricePoints';
import { priceOptionsValid } from '../utils/priceOptions';

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

export const fairMarketValueError = (ticketType) => {
  if (ticketType.free || !ticketType.fair_market_value_two_decimals) {
    return null;
  }

  const fairMarketValue = parseFloatFromString(String(ticketType.fair_market_value_two_decimals));
  const price = parseFloatFromString(String(ticketType.base_price_two_decimals || '0'));
  if (fairMarketValue > price) {
    return `Can't be more than the ticket price (${formatter.format(price)})`;
  }

  return null;
};

export const goodsServicesDescriptionError = (ticketType) => {
  if (ticketType.free || !(parseFloat(ticketType.fair_market_value_two_decimals || 0) > 0)) {
    return null;
  }

  return (ticketType.goods_services_description || '').trim() ? null : 'Describe what the buyer receives';
};

export const canSubmitForm = (ticketType, fairMarketValueEnabled = false) => {
  var minimumTicketsPerOrder;
  if(ticketType.minimum_tickets_per_order){
    minimumTicketsPerOrder = parseFloatFromString(ticketType.minimum_tickets_per_order);
  }

  var maximumTicketsPerOrder;
  if(ticketType.maximum_tickets_per_order){
    maximumTicketsPerOrder = parseFloatFromString(ticketType.maximum_tickets_per_order);
  }

  var startTime = ticketType.start_time;
  var endTime   = ticketType.end_time;

  var availabilityValid = (
    ticketType
      && ticketType.availability
      && (
        ticketType.availability === "Date & Time"
          && startTime
          && startTime <= endTime
          && endTime
      ) || (
        ticketType.availability === "Based on show details"
          && ticketType.start_show_activity_id !== null
          && ticketType.end_show_activity_id !== null
      ) || (
        (
          ticketType.availability === "When sales end for..."
            || ticketType.availability === "When tickets sell out for..."
            || ticketType.availability === "When ticket sales end or sell out for..."
        )
        && ticketType.wait_for_ticket_type_id
        && endTime
      )
  );

  const pricePointsValid = !pricePointsError(ticketType.price_points, ticketType);

  const timedEntryValid =
    !ticketType.timed_entry ||
    (
      ticketType.timed_entry_availability === "Date & Time" &&
      ticketType.timed_entry_start &&
      ticketType.timed_entry_end
    ) ||
    (
      ticketType.timed_entry_availability === "Based on show details" &&
      ticketType.timed_entry_start_show_activity_id &&
      ticketType.timed_entry_end_show_activity_id
    );

  const dynamicPricingValid = (
    !ticketType.dynamic_pricing_enabled ||
    (
      ticketType.dynamic_pricing_min &&
      ticketType.dynamic_pricing_max &&
      parseFloat(ticketType.dynamic_pricing_min) < parseFloat(ticketType.dynamic_pricing_max) &&
      parseFloat(ticketType.dynamic_pricing_min) <= parseFloat(ticketType.base_price_two_decimals) &&
      parseFloat(ticketType.dynamic_pricing_max) >= parseFloat(ticketType.base_price_two_decimals)
    )
  );

  return (
    ticketType
      && ticketType.base_name
      && ticketType.available
      && ticketType.base_price_two_decimals
      && ticketType.settlement_amount_two_decimals
      && minimumTicketsPerOrder
      && maximumTicketsPerOrder
      && minimumTicketsPerOrder <= maximumTicketsPerOrder
      && availabilityValid
      && pricePointsValid
      && timedEntryValid
      && ticketType.check_in_limit >= 0
      && dynamicPricingValid
      && priceOptionsValid(ticketType)
      && (!fairMarketValueEnabled || (!fairMarketValueError(ticketType) && !goodsServicesDescriptionError(ticketType)))
  );
};

export const visiblePricePoints = (pricePoints) => {
  if (!pricePoints) {
    return [];
  }

  return pricePoints
    .filter((pricePoint) => !pricePoint._destroy)
    .sort((first, second) => (
      quantityPricePoint(first) && quantityPricePoint(second)
        ? soldCountThreshold(first) - soldCountThreshold(second)
        : new Date(first.starts_at) - new Date(second.starts_at)
    ));
};

export const minTicketsPerOrderMoreThanMax = (ticketType) => {
  var minimumTicketsPerOrder;
  if(ticketType.minimum_tickets_per_order){
    minimumTicketsPerOrder = parseFloatFromString(ticketType.minimum_tickets_per_order);
  }

  var maximumTicketsPerOrder;
  if(ticketType.maximum_tickets_per_order){
    maximumTicketsPerOrder = parseFloatFromString(ticketType.maximum_tickets_per_order);
  }

  return (
    minimumTicketsPerOrder
      && maximumTicketsPerOrder
      && minimumTicketsPerOrder > maximumTicketsPerOrder
  );
};

export const manualTaxShouldBeChecked = (item, manualTax) => {
  var foundTaxableItem = (item.taxable_items || []).find((taxableItem) =>
    taxableItem.manual_tax_id === manualTax.id
  );

  return (
    foundTaxableItem !== undefined
      && foundTaxableItem
      && !foundTaxableItem._destroy
  );
}

export const calculateLift = (ticketType, ticketTypes) => {
  var bundledId = parseInt(ticketType.bundled_ticket_type_id);
  var bundledTicketType = ticketTypes.find((tt) => tt.id === bundledId);

  var selfAmount = parseFloatFromString(ticketType.settlement_amount_two_decimals || "0.00");
  var bundledAmount = parseFloatFromString(bundledTicketType.settlement_amount_two_decimals || "0.00");
  var calculated = (selfAmount - bundledAmount);

  return (calculated >= 0.00 ? calculated : 0.00);
}

export const parseDateTime = (dateTime) => {
  if (dateTime){
    if (typeof(dateTime) === "string") {
      var dateObj = new Date(dateTime);
      return dateObj;
    } else {
      return dateTime;
    }
  }
}

export const showActivityOption = (showActivity) => {
  const formattedTime = offsetDateForBrowser(showActivity.start_time).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });

  const label = showActivity.set ? (
    `${showActivity.name} - ${formattedTime}` 
  ): (
    <>
      {showActivity.name}
      <span style={{ color: 'var(--sl-text-error)' }}> Date not set yet</span>
    </>
  );

  return {
    value: showActivity.id,
    label,
  }
}

const PRICE_TYPE_NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

export const moveFocusWithoutSelectingPriceType = (e) => {
  if (!PRICE_TYPE_NAVIGATION_KEYS.includes(e.key)) {
    return;
  }

  e.preventDefault();
  e.stopPropagation();

  const tabs = Array.from(e.currentTarget.querySelectorAll('[role="tab"]'));
  const current = tabs.indexOf(document.activeElement);
  const next = e.key === 'Home' ? 0
    : e.key === 'End' ? tabs.length - 1
    : (current + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;

  tabs[next].focus();
};

export const ACCESSIBLE_SEATING_OPTIONS = [
  { value: '', label: 'Not an accessible seat' },
  { value: 'accessible', label: 'Accessible seat' },
  { value: 'companion', label: 'Companion seat' },
];

export const fetchReloadedRootTicketGroup = (team, confirm, rootTicketGroup) => (
  axios.get(`/teams/${team.id}/confirms/${confirm.id}/ticket_types`)
    .then(({ data }) => withTicketTypesFromList(rootTicketGroup, data))
);

const withTicketTypesFromList = (group, ticketTypes) => ({
  ...group,
  ticket_types: ticketTypes.filter((ticketType) => ticketType.ticket_group_id === group.id),
  sub_groups: (group.sub_groups || []).map((subGroup) => withTicketTypesFromList(subGroup, ticketTypes))
});

export const ACCESSIBLE_TICKET_OPTIONS = [
  { value: '', label: 'Not an accessible ticket' },
  { value: 'accessible', label: 'Accessible ticket (e.g. wheelchair area / ADA)' },
  { value: 'companion', label: 'Companion ticket' },
];

const seatMapTicketOnSeatedShow = (confirm, ticketType) => (
  confirm.ticketing_arrangement === 'Seated' && Boolean(ticketType?.seating_chart_category_key)
);

export const ticketDragMove = (confirm, ticketType, fromGroup, over) => {
  if (over?.ticketType) {
    if (fromGroup.id === over.ticketGroup.id) return 'within';
    if (presaleGroupLocked(ticketType, fromGroup, over.ticketGroup) || seatMapTicketOnSeatedShow(confirm, ticketType)) return null;
    return 'between';
  }
  if (!over?.ticketGroupId || presaleGroupLocked(ticketType, fromGroup, null)) return null;
  if (seatMapTicketOnSeatedShow(confirm, ticketType) && over.ticketGroupId !== fromGroup.id) return null;
  return 'into';
};

export const timeOnDate = (date, time) => {
  date = parseDateTime(date);
  time = parseDateTime(time);

  if(!date || !time){
    return null;
  }

  var month = date.getUTCMonth() + 1;
  var day   = date.getUTCDate();
  var year  = date.getUTCFullYear();

  var copiedTime = new Date(time.getTime());
  copiedTime.setFullYear(year, month, day);

  return copiedTime;
};

